class ProductGalleryMarquee extends HTMLElement {
    connectedCallback() {
        this.track = this.querySelector("[data-track]")
        this.dotsContainer = this.querySelector("[data-dots]")
        this.prevButton = this.querySelector("[data-gallery-prev]")
        this.nextButton = this.querySelector("[data-gallery-next]")

        if (!this.track) return

        this.breakpointQuery = window.matchMedia("(min-width: 768px)")
        this.isDesktop = this.breakpointQuery.matches

        this.pointerStart = null
        this.dragThreshold = 8

        const carriedIndex = parseInt(this.dataset.initialIndex, 10)
        this.carriedInitialIndex = Number.isFinite(carriedIndex)
            ? carriedIndex
            : null
        delete this.dataset.initialIndex

        this.bindHandlers()
        this.initCarousel()
        this.watchImages()

        this.breakpointQuery.addEventListener("change", this.onBreakpointChange)
        this.addEventListener("pointerdown", this.onPointerDown)
        this.addEventListener("click", this.onSlideClick)

        this.observeSelectedVariantAttribute()
        this.handleInitialVariantSelection()
    }

    disconnectedCallback() {
        this.breakpointQuery?.removeEventListener(
            "change",
            this.onBreakpointChange
        )
        this.removeEventListener("pointerdown", this.onPointerDown)
        this.removeEventListener("click", this.onSlideClick)
        this.variantObserver?.disconnect()
        this.teardownCarousel()
    }

    bindHandlers() {
        this.onBreakpointChange = this.onBreakpointChange.bind(this)
        this.onPointerDown = this.onPointerDown.bind(this)
        this.onSlideClick = this.onSlideClick.bind(this)
        this.scrollPrev = () => this.emblaApi?.scrollPrev()
        this.scrollNext = () => this.emblaApi?.scrollNext()
    }

    onBreakpointChange(event) {
        const wasDesktop = this.isDesktop
        this.isDesktop = event.matches
        if (this.isDesktop === wasDesktop) return

        const index = this.emblaApi?.selectedScrollSnap() ?? 0

        this.teardownCarousel()
        this.initCarousel()
        this.emblaApi?.scrollTo(index, true)

        this.handleInitialVariantSelection()
    }

    // ---------- carousel ----------
    initCarousel() {
        if (typeof EmblaCarousel !== "function") {
            if (this.emblaRetried) {
                console.warn(
                    "product-gallery-marquee: EmblaCarousel unavailable, gallery inert"
                )
                return
            }
            this.emblaRetried = true
            window.addEventListener("load", () => this.initCarousel(), {
                once: true,
            })
            return
        }

        this.emblaApi = EmblaCarousel(this, {
            axis: "x",
            container: "[data-track]",
            slides: "[data-slide]",
            loop: this.isDesktop ? true : false,
            align: this.isDesktop ? "start" : "center",
        })

        this.emblaApi.on("select", () => this.syncControls())
        this.emblaApi.on("reInit", () => this.syncControls())

        this.emblaApi.on("pointerDown", () =>
            this.classList.add("is-dragging")
        )
        this.emblaApi.on("pointerUp", () =>
            this.classList.remove("is-dragging")
        )

        this.setupDots()
        this.syncControls()

        if (this.carriedInitialIndex !== null) {
            this.emblaApi.scrollTo(this.carriedInitialIndex, true)
        }

        this.prevButton?.addEventListener("click", this.scrollPrev)
        this.nextButton?.addEventListener("click", this.scrollNext)
    }

    teardownCarousel() {
        this.prevButton?.removeEventListener("click", this.scrollPrev)
        this.nextButton?.removeEventListener("click", this.scrollNext)

        this.emblaApi?.destroy()
        this.emblaApi = null
        this.classList.remove("is-dragging")

        if (this.dotsContainer) this.dotsContainer.innerHTML = ""
        this.dots = null
    }

    watchImages() {
        const images = Array.from(this.querySelectorAll("[data-slide] img"))
        let pending = images.filter((image) => !image.complete).length
        if (!pending) return

        images.forEach((image) => {
            if (image.complete) return
            const done = () => {
                pending -= 1
                if (pending <= 0) this.emblaApi?.reInit()
            }
            image.addEventListener("load", done, { once: true })
            image.addEventListener("error", done, { once: true })
        })
    }

    setupDots() {
        if (!this.dotsContainer) return

        this.dots = this.emblaApi.scrollSnapList().map((_, index) => {
            const dot = document.createElement("button")
            dot.type = "button"
            dot.className = "dot"
            dot.setAttribute("aria-label", `Go to image ${index + 1}`)
            dot.addEventListener("click", () => this.emblaApi.scrollTo(index))
            this.dotsContainer.appendChild(dot)
            return dot
        })
    }

    syncControls() {
        if (!this.emblaApi) return

        const selected = this.emblaApi.selectedScrollSnap()
        this.dots?.forEach((dot, index) =>
            dot.classList.toggle("active", index === selected)
        )

        if (this.prevButton) {
            this.prevButton.disabled = !this.emblaApi.canScrollPrev()
        }
        if (this.nextButton) {
            this.nextButton.disabled = !this.emblaApi.canScrollNext()
        }
    }

    // ---------- zoom ----------
    onPointerDown(event) {
        this.pointerStart = { x: event.clientX, y: event.clientY }
    }

    onSlideClick(event) {
        if (event.target.closest("button")) return

        if (this.pointerStart) {
            const travel =
                Math.abs(event.clientX - this.pointerStart.x) +
                Math.abs(event.clientY - this.pointerStart.y)
            this.pointerStart = null
            if (travel > this.dragThreshold) return
        }

        const slide = event.target.closest("[data-slide]")
        if (!slide) return

        const scope = this.closest("#main-product-section") || document
        const zoom = scope.querySelector("product-gallery-zoom")
        if (!zoom || typeof zoom.open !== "function") return

        event.preventDefault()
        zoom.open(parseInt(slide.dataset.slideIndex, 10) || 0)
    }

    // ---------- variant sync ----------
    observeSelectedVariantAttribute() {
        const closestElement = this.closest(
            "[data-product-card-selected_variant_id]"
        )
        if (!closestElement) return

        this.variantObserver = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
                if (
                    mutation.type === "attributes" &&
                    mutation.attributeName ===
                        "data-product-card-selected_variant_id"
                ) {
                    const newValue = closestElement.getAttribute(
                        "data-product-card-selected_variant_id"
                    )
                    if (newValue) this.scrollToVariant(newValue)
                }
            }
        })

        this.variantObserver.observe(closestElement, { attributes: true })
    }

    handleInitialVariantSelection() {
        if (this.carriedInitialIndex !== null) return

        const closestElement = this.closest(
            "[data-product-card-selected_variant_id]"
        )
        const initialVariantId = closestElement?.getAttribute(
            "data-product-card-selected_variant_id"
        )
        if (initialVariantId) this.scrollToVariant(initialVariantId)
    }

    scrollToVariant(variantId) {
        if (!this.emblaApi) return

        const index = this.emblaApi
            .slideNodes()
            .findIndex((slide) => slide.dataset.targetVariant === variantId)
        if (index !== -1) this.emblaApi.scrollTo(index, false)
    }
}

if (!customElements.get("product-gallery-marquee")) {
    customElements.define("product-gallery-marquee", ProductGalleryMarquee)
}
